import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  CreditCard, ShieldCheck, QrCode, Copy, CheckCircle2, Loader2, ChevronLeft, HelpCircle, XCircle 
} from 'lucide-react';
import { toast } from 'sonner';

import logoImg from '../assets/logo-semfrase.png';
import planoFundo from "../assets/planodefundo.png";

const CheckoutPremium = () => {
  const navigate = useNavigate();
  const { user, userProfile, authLoading } = useAuth();
  const [loadingAction, setLoadingAction] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [copied, setCopied] = useState(false);

  const fakePixCode = "00020101021226870014br.gov.bcb.pix2565api.mercadopago.com/v1/pix/qrs/socorreai-premium-activation-key-2026";

  // Se o status mudar para ativo em tempo real, manda para o dashboard do técnico
  useEffect(() => {
    if (userProfile?.paymentStatus === 'active') {
      toast.success("Acesso Premium Liberado!");
      navigate('/technician-dashboard', { replace: true });
    }
  }, [userProfile, navigate]);

  const handleCopyPix = () => {
    navigator.clipboard.writeText(fakePixCode);
    setCopied(true);
    toast.success("Código Copiado!");
    setTimeout(() => setCopied(false), 3000);
  };

  // BOTÃO 1: SIMULA APROVAÇÃO E ADICIONA 30 DIAS
  const handleSimulateActivate = async () => {
    setLoadingAction(true);
    try {
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + 30); // Soma 30 dias na data atual

      await updateDoc(doc(db, 'users', user.uid), {
        paymentStatus: 'active',
        subscriptionExpiresAt: expirationDate.toISOString()
      });
      toast.success("Sucesso: 30 dias adicionados!");
    } catch (error) {
      toast.error("Erro ao salvar no banco.");
    } finally {
      setLoadingAction(false);
    }
  };

  // BOTÃO 2: REMOVE O ACESSO E EXPIROU/PENDENTE
  const handleSimulateDeactivate = async () => {
    setLoadingAction(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        paymentStatus: 'pending',
        subscriptionExpiresAt: "" // Limpa a data de validade
      });
      toast.warning("Acesso removido com sucesso!");
    } catch (error) {
      toast.error("Erro ao atualizar banco.");
    } finally {
      setLoadingAction(false);
    }
  };

  if (authLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#0a0f1a]">
        <Loader2 className="animate-spin text-orange-500" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex grid grid-cols-1 lg:grid-cols-12 font-sans bg-white overflow-x-hidden text-slate-900">
      
      {/* COLUNA ESQUERDA: DETALHES DO PLANO */}
      <div className="hidden lg:flex lg:col-span-5 relative bg-[#0a0f1a] flex-col items-center justify-center p-12 text-center overflow-hidden border-r border-white/5">
        <div className="absolute inset-0 opacity-30 transform scale-110" style={{ backgroundImage: `url(${planoFundo})`, backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-600/15 rounded-full blur-[100px]"></div>
        
        <div className="relative z-20 w-full flex flex-col items-center">
          <div className="bg-white p-8 rounded-full shadow-[0_0_80px_rgba(59,130,246,0.3)] mb-10 border-[12px] border-white/10">
            <img src={logoImg} alt="Socorre Aí" className="w-32 mx-auto" />
          </div>

          <div className="bg-white p-8 rounded-[2.5rem] shadow-2xl w-full max-w-sm mb-6 border border-slate-100 text-left">
            <Badge className="bg-orange-100 text-orange-600 border-none font-black text-[9px] uppercase px-3 py-1 italic mb-4">Acesso Profissional</Badge>
            <h3 className="text-2xl font-black uppercase italic text-slate-900 tracking-tighter mb-4 leading-none">Plano Mensal Parceiro</h3>
            
            <div className="flex items-baseline gap-1 mb-6 border-b border-slate-100 pb-4">
              <span className="text-slate-400 font-black text-xs uppercase italic">R$</span>
              <span className="text-4xl font-black tracking-tighter text-slate-900">49,90</span>
              <span className="text-slate-400 font-bold text-xs uppercase italic">/mês</span>
            </div>

            <ul className="text-slate-700 text-xs font-semibold space-y-4">
              <li className="flex items-center gap-3"><CheckCircle2 className="text-green-500 w-4 h-4 shrink-0"/> Recebimento de chamados diretos</li>
              <li className="flex items-center gap-3"><CheckCircle2 className="text-green-500 w-4 h-4 shrink-0"/> Visibilidade total na listagem de Unaí</li>
              <li className="flex items-center gap-3"><CheckCircle2 className="text-green-500 w-4 h-4 shrink-0"/> Chat integrado em tempo real</li>
              <li className="flex items-center gap-3"><ShieldCheck className="text-blue-600 w-4 h-4 shrink-0"/> Selo de Profissional Verificado ativo</li>
            </ul>
          </div>
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 italic">🔒 Ambiente de pagamento 100% seguro</p>
        </div>
      </div>

      {/* COLUNA DIREITA: OPÇÕES DE PAGAMENTO */}
      <div className="w-full lg:col-span-7 flex items-center justify-center p-6 md:p-12 relative bg-slate-50/50">
        <button type="button" onClick={() => navigate('/dashboard')} className="absolute top-8 left-8 flex items-center gap-2 text-[10px] font-black uppercase text-slate-400 hover:text-orange-500 transition-all"><ChevronLeft size={16}/> Ir para o Dashboard</button>
        
        <div className="w-full max-w-xl text-left space-y-6">
          <header className="mb-8 pb-5 border-b border-slate-200/60">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2 italic">PENDÊNCIA FINANCEIRA DETECTADA <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div></span>
            <h2 className="text-3xl font-black italic uppercase text-slate-900 tracking-tighter mt-1">Ativar Área do Técnico</h2>
            <p className="text-slate-500 font-bold italic text-xs uppercase mt-2">Ative a sua mensalidade via Mercado Pago para liberar o recebimento de ordens de serviço no mapa.</p>
          </header>

          {/* PAINEL DE CONTROLE DE TESTES EXCLUSIVO PEDIDO */}
          <div className="bg-slate-900 text-white p-6 rounded-[2rem] border border-slate-800 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <span className="text-[10px] font-black uppercase text-orange-500 tracking-wider">Modo Sandbox - Painel de Depuração</span>
              <Badge variant="outline" className="text-white border-white/20 uppercase text-[9px] font-bold">Status Atual: {userProfile?.paymentStatus || 'Sem status'}</Badge>
            </div>
            
            <p className="text-[11px] text-slate-400 font-semibold leading-normal">Use os botões de simulação abaixo para forçar os estados de ativação por tempo e testar o sistema de travas das rotas:</p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <Button onClick={handleSimulateActivate} disabled={loadingAction} className="h-12 bg-green-600 hover:bg-green-700 text-white font-black uppercase italic text-[10px] tracking-wider rounded-xl shadow-lg flex gap-2">
                {loadingAction ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
                Injetar +30 Dias (Ativar)
              </Button>
              
              <Button onClick={handleSimulateDeactivate} disabled={loadingAction} className="h-12 bg-red-600 hover:bg-red-700 text-white font-black uppercase italic text-[10px] tracking-wider rounded-xl shadow-lg flex gap-2">
                {loadingAction ? <Loader2 className="animate-spin" size={14} /> : <XCircle size={14} />}
                Remover Pagamento (Bloquear)
              </Button>
            </div>
          </div>

          {!paymentMethod ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card onClick={() => setPaymentMethod('pix')} className="bg-white rounded-[2.5rem] border-2 border-transparent hover:border-blue-600 shadow-xl p-8 cursor-pointer transition-all group flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-2xl bg-teal-50 text-teal-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-inner">
                  <QrCode size={32} />
                </div>
                <h4 className="text-lg font-black uppercase italic text-slate-800">Pagar via PIX</h4>
                <p className="text-[10px] font-bold text-slate-400 uppercase mt-2 italic">Aprovação imediata via API</p>
              </Card>

              <Card onClick={() => setPaymentMethod('card')} className="bg-white rounded-[2.5rem] border-2 border-transparent hover:border-blue-600 shadow-xl p-8 cursor-pointer transition-all group flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-inner">
                  <CreditCard size={32} />
                </div>
                <h4 className="text-lg font-black uppercase italic text-slate-800">Cartão de Crédito</h4>
                <p className="text-[10px] font-bold text-slate-400 uppercase mt-2 italic">Assinatura recorrente invisível</p>
              </Card>
            </div>
          ) : (
            <div className="space-y-6">
              {paymentMethod === 'pix' && (
                <Card className="bg-white rounded-[2.5rem] border-none shadow-2xl p-8 text-center space-y-6 flex flex-col items-center">
                  <Badge className="bg-teal-50 text-teal-600 border-none font-black text-[9px] uppercase px-3 py-1 italic">Pix Copia e Cola Gerado</Badge>
                  
                  <div className="w-44 h-44 bg-slate-100 rounded-3xl border border-slate-200 flex items-center justify-center shadow-inner">
                     <div className="w-40 h-40 bg-slate-200 rounded-2xl flex items-center justify-center text-[10px] font-black text-slate-400 uppercase italic">QR CODE</div>
                  </div>

                  <div className="w-full text-left space-y-2">
                     <label className="text-[9px] font-black uppercase ml-2 text-slate-400 italic">Código Copia e Cola</label>
                     <div className="relative flex items-center">
                       <input readOnly value={fakePixCode} className="w-full h-14 rounded-2xl border border-slate-200 bg-slate-50 text-xs font-mono text-slate-500 px-4 pr-16 font-bold" />
                       <Button onClick={handleCopyPix} className="absolute right-2 h-10 bg-blue-600 text-white font-black text-[10px] uppercase rounded-xl px-4">
                         {copied ? "COPIADO" : <Copy size={14} />}
                       </Button>
                     </div>
                  </div>

                  <Button variant="ghost" onClick={() => setPaymentMethod(null)} className="w-full h-14 rounded-xl border border-slate-200 font-black uppercase italic text-[10px] text-slate-400">ALTERAR FORMA</Button>
                </Card>
              )}

              {paymentMethod === 'card' && (
                <Card className="bg-white rounded-[2.5rem] border-none shadow-2xl p-8 text-center space-y-4">
                  <Badge className="bg-blue-50 text-blue-600 border-none font-black text-[9px] uppercase px-3 py-1 italic mb-2">Cartão via Mercado Pago</Badge>
                  <div className="h-40 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400">
                     <CreditCard size={40} className="mb-2 text-blue-600/40"/>
                     <p className="font-black uppercase italic text-[10px]">Formulário do Mercado Pago</p>
                  </div>
                  <Button variant="ghost" onClick={() => setPaymentMethod(null)} className="w-full h-14 rounded-xl border border-slate-200 font-black uppercase italic text-[10px] text-slate-400">VOLTAR</Button>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutPremium;